/****
 * Interactive guided tour component that introduces users to key dashboard
 * features, manages onboarding walkthroughs, and tracks tour completion
 * preferences across user sessions.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Joyride, { type CallBackProps, type Step, type TooltipRenderProps } from "react-joyride";
import { useDispatch, useSelector } from "react-redux";

import { postUserTourShown } from "@/services/TourGuide";
import { selectShowTour, setShowTour } from "@/store/userSlice";

interface TourGuideProps {
  userPrincipalName: string;
  flag?: number;
}

interface CustomTooltipProps extends TooltipRenderProps {
  onSkip: () => void;
}

const CustomTooltip = ({
  step,
  primaryProps,
  backProps,
  index,
  size,
  onSkip,
}: CustomTooltipProps) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-lg shadow-xl p-5 max-w-sm">
      <div>{step.content}</div>

      <div className="flex items-center justify-between mt-5">
        <button
          type="button"
          onClick={onSkip}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          {t("components.tour.skip")}
        </button>

        <div className="flex gap-2">
          {index > 0 && (
            <button {...backProps} className="px-3 py-1 border rounded text-sm">
              {t("components.tour.previous")}
            </button>
          )}

          <button {...primaryProps} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
            {index === size - 1 ? t("components.tour.finish") : t("components.tour.next")}
          </button>
        </div>
      </div>
    </div>
  );
};

const TourGuide: React.FC<TourGuideProps> = ({ userPrincipalName, flag = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const reduxTrigger = useSelector(selectShowTour);

  const [runTour, setRunTour] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  const renderStepContent = (translationKey: string, defaultValue: string) => {
    const content = t(translationKey, { defaultValue });

    return (
      <div role="tooltip" aria-label={content}>
        {content}
      </div>
    );
  };

  // Start the guided tour when triggered by Redux or configuration flags
  useEffect(() => {
    if (!reduxTrigger && !flag) return;

    if (reduxTrigger) {
      const alreadyStarted = sessionStorage.getItem("tour_started");

      if (alreadyStarted) return;

      sessionStorage.setItem("tour_started", "true");
    }

    dispatch(setShowTour(false));

    setRunTour(false);

    window.scrollTo(0, 0);

    setTimeout(() => {
      setTourKey((k) => k + 1);
      setRunTour(true);
    }, 100);
  }, [reduxTrigger, flag, dispatch]);

  // Prevent page scrolling while the onboarding tour is active
  useEffect(() => {
    document.body.style.overflow = runTour ? "hidden" : "";
    document.documentElement.style.overflow = runTour ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [runTour]);

  const steps: Step[] = useMemo(
    () => [
      {
        target: ".timezone-bar",
        content: renderStepContent(
          "components.tour.timezoneBar",
          "This bar shows current times in key locations across Africa."
        ),
        disableBeacon: true,
      },
      {
        target: ".notification-icon",
        content: renderStepContent(
          "components.tour.notificationIcon",
          "View your notifications here."
        ),
      },
      {
        target: ".language-switcher",
        content: renderStepContent(
          "components.tour.languageSwitcher",
          "Use this menu to change the dashboard language."
        ),
      },
      {
        target: ".profile-menu",
        content: renderStepContent(
          "components.tour.profileMenu",
          "Use this menu to manage your profile and settings."
        ),
      },
    ],
    [t]
  );

  const closeTour = async () => {
    setRunTour(false);

    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";

    dispatch(setShowTour(false));

    sessionStorage.removeItem("tour_started");

    try {
      await postUserTourShown(userPrincipalName);
    } catch {
      // ignore
    }
  };

  const onJoyrideCallback = async (data: CallBackProps) => {
    const { action, status, type } = data;

    if (
      ["close", "reset", "stop"].includes(String(action).toLowerCase()) ||
      ["finished", "skipped"].includes(String(status).toLowerCase())
    ) {
      await closeTour();
      return;
    }
  };

  return (
    <Joyride
      key={tourKey}
      steps={steps}
      run={runTour}
      callback={onJoyrideCallback}
      continuous
      hideCloseButton
      disableCloseOnEsc
      disableOverlayClose={false}
      disableScrolling
      scrollToFirstStep
      spotlightPadding={6}
      debug
      tooltipComponent={(props) => (
        <CustomTooltip
          {...props}
          onSkip={() => {
            closeTour();
          }}
        />
      )}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: "#007bff",
        },
      }}
      locale={{
        last: t("components.tour.finish", {
          defaultValue: "Finish",
        }),
        next: t("components.tour.next", {
          defaultValue: "Next",
        }),
        back: t("components.tour.previous", {
          defaultValue: "Previous",
        }),
      }}
    />
  );
};

export default TourGuide;
